/**
 * Gastos Page — Card-based UI
 * src/pages/Gastos.jsx
 *
 * Shows expenses as card list with filters, click-to-edit modal, Firestore sync.
 * Seeds data on first load if collection is empty.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
} from 'firebase/firestore';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { showToast } from '../components/Toast';
import { useSettings } from '../context/SettingsContext';
import { logActivity } from '../utils/auditLog';

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

const fmt = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(amount || 0);

const SEED_GASTOS = [
  { person: 'Tumi',    categoria: 'Caja de Ahorro',            esencial: 'Si', medioPago: 'Nomina',  monto: 13349.16, payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Carmen',                    esencial: 'Si', medioPago: 'Debito',  monto: 13000,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Bupa',                      esencial: 'Si', medioPago: 'Debito',  monto: 10000,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Vacaciones',                esencial: 'Si', medioPago: 'Debito',  monto: 12500,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Gym & Entrenador',          esencial: 'Si', medioPago: 'Debito',  monto: 23000,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Takiro',                    esencial: 'Si', medioPago: 'Debito',  monto: 5000,     payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Servicios',                 esencial: 'Si', medioPago: 'TDC',     monto: 2500,     payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Gasolina',                  esencial: 'Si', medioPago: 'Debito',  monto: 1000,     payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Comida',                    esencial: 'Si', medioPago: 'Debito',  monto: 10000,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Prestamos',                 esencial: 'Si', medioPago: 'Nomina',  monto: 46000,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Cuota Be Grand',            esencial: 'Si', medioPago: 'Debito',  monto: 35730,    payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Mani Pedi',                 esencial: 'No', medioPago: 'Debito',  monto: 2000,     payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Pelu',                      esencial: 'No', medioPago: 'Debito',  monto: 3000,     payee: '', moneda: 'MXN' },
  { person: 'Tumi',    categoria: 'Gastos Pequenos Diarios',   esencial: 'No', medioPago: 'Debito',  monto: 3000,     payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'ITESM Camila',              esencial: 'Si', medioPago: 'TDC',     monto: 20000,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'ITESM Tony',                esencial: 'Si', medioPago: 'TDC',     monto: 47100,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Bupa',                      esencial: 'Si', medioPago: 'TDC',     monto: 19400,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Renta MTY',                 esencial: 'Si', medioPago: 'Debito',  monto: 14500,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Mensualidad Camila y Tony', esencial: 'Si', medioPago: 'Debito',  monto: 16000,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'AT&T',                      esencial: 'Si', medioPago: 'TDC',     monto: 5230,     payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Servicios',                 esencial: 'Si', medioPago: 'TDC',     monto: 2500,     payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Gasolina',                  esencial: 'Si', medioPago: 'TDC',     monto: 1000,     payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Comida',                    esencial: 'Si', medioPago: 'TDC',     monto: 10000,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Starlink',                  esencial: 'Si', medioPago: 'TDC',     monto: 255,      payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'EALDE (256 Euros)',          esencial: 'Si', medioPago: 'TDC',     monto: 5420,     payee: 'EALDE', moneda: 'EUR' },
  { person: 'Alberto', categoria: 'Netflix',                   esencial: 'Si', medioPago: 'TDC',     monto: 442,      payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'LinkedIn',                  esencial: 'Si', medioPago: 'TDC',     monto: 600,      payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Youtube',                   esencial: 'Si', medioPago: 'TDC',     monto: 150,      payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Prestamo Tumi',             esencial: 'Si', medioPago: 'Debito',  monto: 76000,    payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Barber',                    esencial: 'No', medioPago: 'TDC',     monto: 3000,     payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Salidas',                   esencial: 'No', medioPago: 'TDC',     monto: 5000,     payee: '', moneda: 'MXN' },
  { person: 'Alberto', categoria: 'Uber',                      esencial: 'No', medioPago: 'TDC',     monto: 3000,     payee: '', moneda: 'MXN' },
];

async function seedGastos(db) {
  const snap = await getDocs(collection(db, 'gastos'));
  if (snap.empty) {
    for (const item of SEED_GASTOS) {
      await addDoc(collection(db, 'gastos'), item);
    }
    const newSnap = await getDocs(collection(db, 'gastos'));
    return newSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

const MEDIO_PAGO_OPTIONS = ['Nomina', 'Debito', 'TDC', 'Efectivo'];
const MONEDA_OPTIONS = ['MXN', 'USD', 'EUR'];

const personColor = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

const BLANK_MODAL = {
  id: null, person: 'Tumi', categoria: '', esencial: 'Si',
  medioPago: 'Debito', payee: '', moneda: 'MXN', monto: 0, saving: false,
};

export default function Gastos({ db, user }) {
  const { t } = useSettings();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('Todos');
  const [modal, setModal]     = useState(null); // null | row object with id

  useEffect(() => {
    (async () => {
      try {
        const data = await seedGastos(db);
        setRows(data);
      } catch (err) {
        console.error('Error loading gastos:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [db]);

  const filteredRows = useMemo(
    () => (filter === 'Todos' ? rows : rows.filter((r) => r.person === filter)),
    [rows, filter]
  );

  const totalGeneral    = filteredRows.reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const totalEsencial   = filteredRows.filter((r) => r.esencial === 'Si').reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const totalNoEsencial = filteredRows.filter((r) => r.esencial === 'No').reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const totalTumi       = filteredRows.filter((r) => r.person === 'Tumi').reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const totalAlberto    = filteredRows.filter((r) => r.person === 'Alberto').reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);

  const openAdd = () => setModal({ ...BLANK_MODAL, person: filter !== 'Todos' ? filter : 'Tumi' });
  const openEdit = (row) => setModal({
    id: row.id, person: row.person, categoria: row.categoria || '',
    esencial: row.esencial || 'Si', medioPago: row.medioPago || 'Debito',
    payee: row.payee || '', moneda: row.moneda || 'MXN',
    monto: row.monto || 0, saving: false,
  });

  const handleSave = async () => {
    if (!modal) return;
    setModal(m => ({ ...m, saving: true }));
    try {
      const data = {
        person: modal.person,
        categoria: modal.categoria,
        esencial: modal.esencial,
        medioPago: modal.medioPago,
        payee: modal.payee || '',
        moneda: modal.moneda || 'MXN',
        monto: parseFloat(modal.monto) || 0,
      };
      if (modal.id) {
        await updateDoc(doc(db, 'gastos', modal.id), data);
        setRows(prev => prev.map(r => r.id === modal.id ? { ...r, ...data } : r));
        await logActivity(db, user, 'editar', 'Gasto', { persona: modal.person, categoria: modal.categoria, monto: modal.monto, moneda: modal.moneda || 'MXN' });
        showToast('Gasto actualizado');
      } else {
        const ref = await addDoc(collection(db, 'gastos'), data);
        setRows(prev => [...prev, { id: ref.id, ...data }]);
        await logActivity(db, user, 'crear', 'Gasto', { persona: modal.person, categoria: modal.categoria, monto: modal.monto, moneda: modal.moneda || 'MXN' });
        showToast('Gasto agregado');
      }
      setModal(null);
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setModal(m => ({ ...m, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!modal?.id) return;
    try {
      await deleteDoc(doc(db, 'gastos', modal.id));
      setRows(prev => prev.filter(r => r.id !== modal.id));
      await logActivity(db, user, 'eliminar', 'Gasto', { persona: modal.person, categoria: modal.categoria, monto: modal.monto });
      setModal(null);
      showToast('Gasto eliminado');
    } catch (err) {
      console.error(err);
      showToast('Error al eliminar', 'error');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="app-loading" style={{ minHeight: '40vh' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  return (
    <div className="page-container">
      {/* Title + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t('gastos')} <span style={mxnBadge}>MXN</span></h1>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#1a472a', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 0.875rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          <Plus size={16} /> {t('add')}
        </button>
      </div>

      {/* Summary cards — Row 1: Total General */}
      <div style={{ marginBottom: '0.5rem' }}>
        <div className="summary-card" style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '0.75rem', padding: '0.875rem 1.125rem' }}>
          <div className="summary-card-label">Total General</div>
          <div className="summary-card-value" style={{ color: '#ef4444', fontSize: '1.3rem' }}>{fmt(totalGeneral)}</div>
        </div>
      </div>
      {/* Row 2: Esenciales | No Esenciales */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <div className="summary-card" style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: '0.75rem' }}>
          <div className="summary-card-label">Esenciales</div>
          <div className="summary-card-value" style={{ color: '#10b981' }}>{fmt(totalEsencial)}</div>
        </div>
        <div className="summary-card" style={{ background: 'rgba(107,114,128,0.07)', border: '1px solid rgba(107,114,128,0.15)', borderRadius: '0.75rem' }}>
          <div className="summary-card-label">No Esenciales</div>
          <div className="summary-card-value" style={{ color: '#6b7280' }}>{fmt(totalNoEsencial)}</div>
        </div>
      </div>
      {/* Row 3: Tumi | Alberto */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div className="summary-card" style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: '0.75rem' }}>
          <div className="summary-card-label" style={{ color: '#8b5cf6' }}>Tumi</div>
          <div className="summary-card-value" style={{ color: '#8b5cf6' }}>{fmt(totalTumi)}</div>
        </div>
        <div className="summary-card" style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: '0.75rem' }}>
          <div className="summary-card-label" style={{ color: '#3b82f6' }}>Alberto</div>
          <div className="summary-card-value" style={{ color: '#3b82f6' }}>{fmt(totalAlberto)}</div>
        </div>
      </div>

      {/* Filter buttons */}
      <div className="filter-buttons" style={{ marginBottom: '0.75rem' }}>
        {['Todos', 'Tumi', 'Alberto'].map((f) => (
          <button key={f} className={`filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filteredRows.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Sin gastos. Agrega uno con el boton +.
          </div>
        )}

        {filteredRows.map(row => (
          <div key={row.id} onClick={() => openEdit(row)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: '1px solid var(--border-color)', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}>
            {/* Person color bar */}
            <div style={{ width: 4, height: 44, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />

            {/* Center info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person) }}>{row.person}</span>
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.35rem', borderRadius: '0.25rem',
                  background: row.esencial === 'Si' ? 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.12)',
                  color: row.esencial === 'Si' ? '#10b981' : '#6b7280',
                }}>
                  {row.esencial === 'Si' ? 'Esencial' : 'No Esencial'}
                </span>
                {row.moneda && row.moneda !== 'MXN' && (
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.35rem', borderRadius: '0.25rem', background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
                    {row.moneda}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.categoria}
              </div>
              <div style={{ fontSize: '0.73rem', color: 'var(--text-light)', marginTop: '0.15rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ background: 'var(--bg-tertiary)', borderRadius: '0.25rem', padding: '0.05rem 0.35rem' }}>{row.medioPago}</span>
                {row.payee && <span>{row.payee}</span>}
              </div>
            </div>

            {/* Right: amount */}
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ef4444' }}>{fmt(row.monto)}</div>
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              {modal.id ? t('edit') : t('add')} Gasto
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>{t('person')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${modal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: modal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label style={labelStyle}>{t('category')}</label>
                <input type="text" value={modal.categoria} onChange={e => setModal(m => ({ ...m, categoria: e.target.value }))} placeholder="Ej. Comida, Gym, Netflix..." style={inputStyle} />
              </div>

              {/* Esencial toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.875rem', background: 'var(--bg-tertiary)', borderRadius: '0.5rem' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t('essential')}</span>
                <button onClick={() => setModal(m => ({ ...m, esencial: m.esencial === 'Si' ? 'No' : 'Si' }))}
                  style={{ width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: modal.esencial === 'Si' ? '#10b981' : 'var(--border-color)', position: 'relative', transition: 'background 0.2s' }}>
                  <span style={{ position: 'absolute', top: 3, left: modal.esencial === 'Si' ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
                </button>
              </div>

              {/* Medio de Pago */}
              <div>
                <label style={labelStyle}>{t('medioPago')}</label>
                <select value={modal.medioPago} onChange={e => setModal(m => ({ ...m, medioPago: e.target.value }))} style={inputStyle}>
                  {MEDIO_PAGO_OPTIONS.map(op => <option key={op} value={op}>{op}</option>)}
                </select>
              </div>

              {/* Payee */}
              <div>
                <label style={labelStyle}>{t('payee')}</label>
                <input type="text" value={modal.payee} onChange={e => setModal(m => ({ ...m, payee: e.target.value }))} placeholder="Ej. Walmart, EALDE, Amazon..." style={inputStyle} />
              </div>

              {/* Moneda + Monto */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: '0 0 90px' }}>
                  <label style={labelStyle}>{t('currency')}</label>
                  <select value={modal.moneda} onChange={e => setModal(m => ({ ...m, moneda: e.target.value }))} style={inputStyle}>
                    {MONEDA_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{t('budget')}</label>
                  <input type="number" inputMode="decimal" value={modal.monto} onChange={e => setModal(m => ({ ...m, monto: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {modal.id && (
                <button onClick={handleDelete}
                  style={{ padding: '0.7rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Trash2 size={15} />{t('delete')}
                </button>
              )}
              <button onClick={() => setModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                {t('cancel')}
              </button>
              <button onClick={handleSave} disabled={modal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: modal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: modal.saving ? 0.7 : 1 }}>
                {modal.saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
